import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";
import {
  CatalogCategory,
  CatalogItem,
  CatalogModifierList,
  CatalogModifier,
  Enum_Catalogmodifierlist_Selection_Type,
  CatalogItemVariation,
  CatalogTax,
} from "../queries/auto-generated/schemas";
import { BuiltItem, ModificationSelections } from "../types";

interface CatalogDataInitialSliceState {
  categories: { [key: string]: CatalogCategory };
  items: { [key: string]: CatalogItem };
  variations: { [key: string]: CatalogItemVariation };
  modifierList: { [key: string]: CatalogModifierList };
  modifiers: { [key: string]: CatalogModifier };
  taxes: {[key: string]: CatalogTax};
}

interface CatalogInitialSliceState {
  data: CatalogDataInitialSliceState;
  error?: any;
  loading: boolean;
  currentItemSelectionChoices?: BuiltItem;
}

const initialState: CatalogInitialSliceState = {
  data: {
    categories: {},
    items: {},
    variations: {},
    modifierList: {},
    modifiers: {},
    taxes: {}
  },
  error: undefined,
  loading: true,
};

const catalog = createSlice({
  name: "catalog",
  initialState,
  reducers: {
    load(
      state,
      action: PayloadAction<{
        data?: {
          catalogCategories?: CatalogCategory[];
          catalogItems?: CatalogItem[];
          catalogItemVariations?: CatalogItemVariation[];
          catalogModifierLists?: CatalogModifierList[];
          catalogModifiers?: CatalogModifier[];
          catalogTaxes?: CatalogTax[];
        };
        error: any;
      }>
    ) {
      if (action.payload?.error) {
        state.error = action.payload?.error;
        return;
      } else {
        state.error = undefined;
      }

      if (!action.payload.data) {
        state.error = "no data"
        return
      } else {
        state.error = undefined;
      }

      if (action.payload.data?.catalogCategories) {
        state.data.categories = _.keyBy(
          action.payload.data?.catalogCategories,
          (o) => {
            return o?.id;
          }
        );
      }

      const { catalogItemVariations, catalogItems } = action.payload.data

      if (catalogItemVariations && catalogItems) {
        const itemsWithVariations: { [key: string]: CatalogItem } = {}

        // Associate each catalog item with its variations and save item only if it has variations
        for (let item of catalogItems) {
          // Search on Square ids
          const foundVariations = catalogItemVariations.filter(x => x.item_id === item.square_id)

          if (foundVariations.length) {
            itemsWithVariations[item.id] = {
              ...item,
              variations: foundVariations
            }
          }
        }
        state.data.items = itemsWithVariations

        state.data.variations = _.keyBy(
          catalogItemVariations,
          (o) => {
            return o?.id;
          }
        );
      }
      if (action.payload.data?.catalogModifierLists) {
        state.data.modifierList = _.keyBy(
          action.payload.data?.catalogModifierLists,
          (o) => {
            return o?.id;
          }
        );
      }
      if (action.payload.data?.catalogModifiers) {
        state.data.modifiers = _.keyBy(
          action.payload.data?.catalogModifiers,
          (o) => {
            return o?.id;
          }
        );
      }
      if (action.payload.data?.catalogTaxes) {
        state.data.taxes = _.keyBy(
          action.payload.data?.catalogTaxes,
          (o) => {
            return o?.id;
          }
        );
      }

      state.loading = false;
    },
    initCurrentItemSelectionChoices(
      state,
      action: PayloadAction<{
        itemId: string;
      }>
    ) {
      const catalogItem = state.data?.items[action.payload.itemId];
      state.currentItemSelectionChoices = setDefaults(catalogItem, state);
    },
    loadCurrentItemSelectionChoices(
      state,
      action: PayloadAction<{ builtItem: BuiltItem }>
    ) {
      state.currentItemSelectionChoices = action.payload.builtItem;
    },
    cancelSelection(state) {
      state.currentItemSelectionChoices = undefined;
    },
    updatedSelection(
      state,
      action: PayloadAction<{
        itemId: string;
        modifierListInfoId: string;
        modifierId: string;
        selectionType: Enum_Catalogmodifierlist_Selection_Type;
      }>
    ) {
      const current = state.currentItemSelectionChoices;
      const { itemId, modifierListInfoId, modifierId } = action.payload;

      if (current && current.item_id === itemId) {
        const singleType =
          action.payload.selectionType ===
          Enum_Catalogmodifierlist_Selection_Type.Single;
        const info = current.modifications[modifierListInfoId]; // TODO: info could be undefined
        const index = info.selected.indexOf(modifierId) ?? -1;

        if (index !== -1) {
          info.selected.splice(index, 1);
        } else {
          singleType
            ? (info.selected = [modifierId])
            : info.selected.push(modifierId);
        }
      }
    },
    updateSelectionVariation(
      state,
      action: PayloadAction<{ itemId: string; variationId: string }>
    ) {
      const current = state.currentItemSelectionChoices;
      const { itemId, variationId } = action.payload;

      if (current && current.item_id === itemId) {
        // TODO: Consider checking if variation is valid for this item id
        current.variation_id = variationId;
      }
    },
    updateSelectionQuantity(state, action: PayloadAction<{ change: number }>) {
      const prevChoices = state.currentItemSelectionChoices;
      if (prevChoices && typeof prevChoices.quantity === "number") {
        const newQuantity = prevChoices.quantity + action.payload.change;
        prevChoices.quantity = Math.max(1, newQuantity);
      }
    },
    updateSelectionNote(state, action: PayloadAction<{ note: string }>) {
      const prevChoices = state.currentItemSelectionChoices;
      if (prevChoices) {
        prevChoices.note = action.payload.note;
      }
    },
    updateSelectionError(state, action: PayloadAction<{ error: boolean }>) {
      if (state.currentItemSelectionChoices) {
        state.currentItemSelectionChoices.error = action.payload.error;
      }
    },
  },
});

const setDefaults = (
  catalogItem: CatalogItem,
  state: CatalogInitialSliceState
): BuiltItem => {
  const modifier_list_info = catalogItem?.modifier_list_info ?? [];

  const variation_id = catalogItem?.variations?.[0]?.id ?? "";
  let modifications: ModificationSelections = {};

  modifier_list_info.forEach((info) => {
    const overrides = info?.modifier_overrides ?? [];

    const default_ids = overrides
      .filter((override) => override?.on_by_default && override?.modifier_id)
      .map((override) => override?.modifier_id ?? "");

    if (default_ids.length === 0) {
      const modifier_list =
        state.data.modifierList[info?.modifier_list?.id ?? ""];
      if (
        !!modifier_list &&
        modifier_list.selection_type ===
        Enum_Catalogmodifierlist_Selection_Type.Single
      ) {
        const modifiers =
          modifier_list.modifiers
            ?.map((mod) => state.data.modifiers[mod?.id ?? ""])
            .sort((a, b) => (a?.ordinal ?? 0) - (b?.ordinal ?? 0)) ?? [];
        if (modifiers.length > 0) {
          default_ids.push(modifiers[0].square_id);
        }
      }
    }

    modifications[info?.id ?? ""] = {
      modifier_list_info_id: info?.id ?? "",
      selected: default_ids,
    };
  });

  return {
    item_id: catalogItem.id,
    modifications,
    variation_id,
    quantity: 1,
  };
};

export default catalog;
